import { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle2, AlertTriangle } from 'lucide-react';
import { C, FONTS } from '../../tokens/design';
import { useBranding } from '../../context/BrandingContext';

const API_BASE = import.meta.env.VITE_API_BASE_URL || '';

const ROLE_LABELS = {
  0: 'Admin',
  1: 'Analyst',
  Admin: 'Admin',
  Analyst: 'Analyst',
  MspAdmin: 'MSP Admin',
  PartnerAdmin: 'MSP Admin',
  ClientUser: 'Viewer',
};

function roleLabel(role) {
  if (role == null) return '';
  return ROLE_LABELS[role] ?? String(role);
}

export default function AcceptInvite() {
  const branding = useBranding();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  const [invite, setInvite] = useState(null);
  const [validating, setValidating] = useState(true);
  const [validateError, setValidateError] = useState('');

  const [displayName, setDisplayName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [done, setDone] = useState(false);

  const btnGradient = `linear-gradient(135deg, ${branding?.secondaryColor || '#e94560'}, ${branding?.primaryColor || '#1a1a2e'})`;

  useEffect(() => {
    if (!token) {
      setValidating(false);
      setValidateError('Missing invite token. Check the link in your email.');
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const resp = await fetch(`${API_BASE}/team/invite/validate?token=${encodeURIComponent(token)}`);
        const data = await resp.json().catch(() => ({}));
        if (cancelled) return;
        if (!resp.ok) {
          setValidateError(data.error || 'This invite link is no longer valid.');
          return;
        }
        if (data.isExpired) {
          setValidateError('This invite has expired. Ask your administrator to send a new one.');
          return;
        }
        if (data.isAlreadyAccepted) {
          setValidateError('This invite has already been accepted. Please sign in.');
          return;
        }
        setInvite(data);
        setDisplayName(data.displayName || '');
      } catch (err) {
        if (!cancelled) setValidateError(err.message || 'Failed to validate invite.');
      } finally {
        if (!cancelled) setValidating(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const handleAccept = async (e) => {
    e.preventDefault();
    if (!displayName.trim()) { setSubmitError('Please enter your name.'); return; }
    setSubmitError('');
    setSubmitting(true);
    try {
      const resp = await fetch(`${API_BASE}/team/invite/accept`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, displayName: displayName.trim() }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(data.error || 'Unable to accept invitation. It may be expired or already used.');
      setDone(true);
      setTimeout(() => navigate('/login'), 4000);
    } catch (err) {
      setSubmitError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const inputStyle = {
    width: '100%', background: C.s2, border: `1px solid ${C.b1}`, borderRadius: 8,
    padding: '12px 14px', color: C.t1, fontSize: 13, fontFamily: FONTS.mono, outline: 'none',
    boxSizing: 'border-box',
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: C.bg }}>
      <div style={{ width: 420 }}>
        <div style={{ fontSize: 22, fontWeight: 800, color: C.t1, fontFamily: FONTS.sans, marginBottom: 6 }}>Accept Team Invitation</div>
        <div style={{ fontSize: 12, color: C.t2, fontFamily: FONTS.mono, marginBottom: 28 }}>Join your team on SentinelCommand</div>

        {validating && (
          <div style={{ padding: 20, background: C.s1, border: `1px solid ${C.b1}`, borderRadius: 10, textAlign: 'center' }}>
            <div style={{ fontSize: 12, color: C.t2, fontFamily: FONTS.mono }}>Validating invite...</div>
          </div>
        )}

        {!validating && validateError && (
          <div style={{ padding: 20, background: C.s1, border: `1px solid ${C.b1}`, borderRadius: 10, textAlign: 'center' }}>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 12, color: C.red }}><AlertTriangle size={36} strokeWidth={1.5} /></div>
            <div style={{ fontSize: 14, fontWeight: 700, color: C.red, fontFamily: FONTS.sans, marginBottom: 8 }}>Invite Not Valid</div>
            <div style={{ fontSize: 11, color: C.t2, fontFamily: FONTS.mono, marginBottom: 20 }}>{validateError}</div>
            <Link to="/login" style={{
              display: 'inline-block', padding: '10px 24px', borderRadius: 8, textDecoration: 'none',
              background: btnGradient, color: '#fff', fontSize: 13, fontWeight: 700, fontFamily: FONTS.sans,
            }}>Go to Sign In</Link>
          </div>
        )}

        {!validating && !validateError && done && (
          <div style={{ padding: 20, background: C.s1, border: `1px solid ${C.b1}`, borderRadius: 10, textAlign: 'center' }}>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 12, color: C.grn }}><CheckCircle2 size={36} strokeWidth={1.5} /></div>
            <div style={{ fontSize: 14, fontWeight: 700, color: C.grn, fontFamily: FONTS.sans, marginBottom: 8 }}>Invitation Accepted</div>
            <div style={{ fontSize: 11, color: C.t2, fontFamily: FONTS.mono, marginBottom: 12, lineHeight: 1.5 }}>
              Check your email for a password setup link. Once you set your password, you can sign in.
            </div>
            <div style={{ fontSize: 10, color: C.t3, fontFamily: FONTS.mono, marginBottom: 20, lineHeight: 1.5 }}>
              After signing in, you can onboard your first client from the dashboard.
            </div>
            <Link to="/login" style={{
              display: 'inline-block', padding: '10px 24px', borderRadius: 8, textDecoration: 'none',
              background: btnGradient, color: '#fff', fontSize: 13, fontWeight: 700, fontFamily: FONTS.sans,
            }}>Go to Sign In</Link>
          </div>
        )}

        {!validating && !validateError && !done && invite && (
          <form onSubmit={handleAccept}>
            <div style={{ padding: 16, background: C.s1, border: `1px solid ${C.b1}`, borderRadius: 10, marginBottom: 20 }}>
              <div style={{ fontSize: 11, color: C.t2, fontFamily: FONTS.mono, marginBottom: 10 }}>You've been invited to join</div>
              <div style={{ fontSize: 16, fontWeight: 700, color: C.t1, fontFamily: FONTS.sans, marginBottom: 12 }}>{invite.mspName || 'your team'}</div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, fontFamily: FONTS.mono, color: C.t2, paddingTop: 10, borderTop: `1px solid ${C.b2}` }}>
                <span>{invite.email}</span>
                <span style={{ color: C.sky }}>{roleLabel(invite.role)}</span>
              </div>
            </div>

            {submitError && (
              <div style={{ padding: '10px 14px', borderRadius: 8, background: C.redBg, border: `1px solid ${C.red}33`, marginBottom: 16, fontSize: 11, color: C.red, fontFamily: FONTS.mono }}>{submitError}</div>
            )}

            <div style={{ marginBottom: 20 }}>
              <label style={{ fontSize: 10, color: C.t2, fontFamily: FONTS.mono, textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: 6 }}>Your Name</label>
              <input value={displayName} onChange={e => setDisplayName(e.target.value)} placeholder="Jane Smith" autoFocus required style={inputStyle} />
            </div>

            <button type="submit" disabled={submitting} style={{
              width: '100%', padding: '13px 0', borderRadius: 8, border: 'none', cursor: submitting ? 'wait' : 'pointer',
              background: submitting ? C.t3 : btnGradient,
              color: '#fff', fontSize: 14, fontWeight: 700, fontFamily: FONTS.sans,
            }}>{submitting ? 'Accepting...' : 'Accept Invitation'}</button>

            <div style={{ fontSize: 10, color: C.t3, fontFamily: FONTS.mono, marginTop: 14, textAlign: 'center', lineHeight: 1.5 }}>
              After accepting, you'll receive an email to set your password.
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
